/**
 * Reusable confirmation modal — replaces the native browser `confirm()` on
 * destructive paths so the UX stays inside the QorTrace aesthetic and
 * survives headless / iframe contexts where `window.confirm` is suppressed.
 *
 * Two ways to use it:
 *
 *   1. Imperative (one-liner) — recommended for ad-hoc destructive paths:
 *
 *        const confirmAction = useConfirmModal();
 *        ...
 *        if (!await confirmAction({
 *          title: "Promote winning challenger?",
 *          description: "This sets it as the new control...",
 *          confirmLabel: "Promote now",
 *          tone: "danger",
 *        })) return;
 *
 *      Mount `<ConfirmModalHost />` once at the root of the page that uses
 *      the hook — typically inside the layout component.
 *
 *   2. Declarative — for cases where the parent already manages open/close:
 *
 *        <ConfirmModal open={open} onCancel={...} onConfirm={...}
 *                      title="..." description="..." />
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";
import { AlertTriangle, X } from "lucide-react";

const TONE_STYLES = {
  danger: {
    accent:  "var(--qt-red, #ef4444)",
    confirmBg:    "rgba(239,68,68,0.15)",
    confirmBd:    "var(--qt-red, #ef4444)",
    confirmText:  "var(--qt-red, #ef4444)",
    iconColor:    "var(--qt-red, #ef4444)",
  },
  warning: {
    accent: "var(--qt-amber, #f59e0b)",
    confirmBg:    "rgba(245,158,11,0.15)",
    confirmBd:    "var(--qt-amber, #f59e0b)",
    confirmText:  "var(--qt-amber, #f59e0b)",
    iconColor:    "var(--qt-amber, #f59e0b)",
  },
  primary: {
    accent: "var(--qt-cyan, #00E5FF)",
    confirmBg:    "rgba(0,229,255,0.15)",
    confirmBd:    "var(--qt-cyan, #00E5FF)",
    confirmText:  "var(--qt-cyan, #00E5FF)",
    iconColor:    "var(--qt-cyan, #00E5FF)",
  },
};

export function ConfirmModal({
  open,
  title = "Are you sure?",
  description = "",
  confirmLabel = "Confirm",
  cancelLabel  = "Cancel",
  tone = "danger",
  onConfirm,
  onCancel,
  testId = "confirm-modal",
}) {
  const styles = TONE_STYLES[tone] || TONE_STYLES.danger;
  const cancelRef = useRef(null);

  // Autofocus cancel + ESC closes + body scroll lock.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
      if (e.key === "Enter")  onConfirm?.();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => cancelRef.current?.focus(), 30);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: "rgba(2,8,15,0.78)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border p-6"
        style={{
          background: "rgba(8,14,22,0.96)",
          borderColor: styles.accent,
          boxShadow: `0 0 0 1px ${styles.accent}33, 0 24px 60px rgba(0,0,0,0.6)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          data-testid={`${testId}-close`}
          onClick={onCancel}
          className="absolute right-4 top-4 opacity-60 hover:opacity-100 transition"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-4">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{
              background: styles.confirmBg,
              border: `1px solid ${styles.confirmBd}`,
            }}
          >
            <AlertTriangle size={18} style={{ color: styles.iconColor }} />
          </div>
          <div className="min-w-0">
            <h2
              id={`${testId}-title`}
              className="text-lg font-semibold leading-tight"
              data-testid={`${testId}-title`}
            >
              {title}
            </h2>
            {description && (
              <p
                className="mt-2 text-sm opacity-80"
                data-testid={`${testId}-description`}
              >
                {description}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            data-testid={`${testId}-cancel`}
            className="rounded-md border px-4 py-2 text-sm transition hover:opacity-90"
            style={{
              borderColor: "rgba(255,255,255,0.16)",
              background: "transparent",
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid={`${testId}-confirm`}
            className="rounded-md border px-4 py-2 text-sm font-medium transition hover:opacity-90"
            style={{
              borderColor: styles.confirmBd,
              background: styles.confirmBg,
              color: styles.confirmText,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Imperative hook + host component.
// ---------------------------------------------------------------------------

const ConfirmCtx = createContext(null);

export function ConfirmModalProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: {}, resolver: null });

  const ask = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, opts, resolver: resolve });
    });
  }, []);

  const close = useCallback((value) => {
    setState((prev) => {
      prev.resolver?.(value);
      return { open: false, opts: {}, resolver: null };
    });
  }, []);

  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      <ConfirmModal
        open={state.open}
        title={state.opts.title}
        description={state.opts.description}
        confirmLabel={state.opts.confirmLabel}
        cancelLabel={state.opts.cancelLabel}
        tone={state.opts.tone}
        testId={state.opts.testId || "confirm-modal"}
        onConfirm={() => close(true)}
        onCancel={()  => close(false)}
      />
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Defensive fallback — if the provider wasn't mounted, drop back to
    // the native confirm() rather than throwing. This keeps every existing
    // destructive path functional even before its parent layout is wrapped.
    return (opts = {}) => Promise.resolve(window.confirm(
      [opts.title, opts.description].filter(Boolean).join("\n\n") || "Confirm?",
    ));
  }
  return ctx;
}
